import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ActionDialogButtons,
  ActionDialogContent,
  AssessmentAttemptResult,
  AssessmentMode,
  Button,
  ButtonGroup,
  ButtonLink,
  Dialog,
  DialogContent,
  MultipleChoiceAssessment,
  QuestionAnswerType,
} from '@opositandopn/ui';
import classnames from 'classnames';
import {
  IoArrowForwardCircleOutline,
  IoDocumentTextOutline,
} from 'react-icons/io5';

import ManualImg from '../../../../assets/images/3f54667a-092c-477e-bb6e-624882625df0.webp';
import InteractiveImg from '../../../../assets/images/5344a3f1-c622-4889-b30e-85db314492aa.webp';
import { Loading } from '../../../../components/core/Loading';
import { useConfiguration } from '../../../../context/ConfigurationContext';
import { useProfile } from '../../../../context/ProfileContext';
import { BaseComponentProps } from '../../../../types/base-component.types';
import {
  MultipleChoiceQuestionViewModel,
  useBookmarkMultipleChoiceQuestion,
  useUnbookmarkMultipleChoiceQuestion,
} from '../../../questions/api/useMultipleChoiceQuestions';
import {
  AssessmentAttemptViewModel,
  CreateMultipleChoiceAssessmentAttemptCommandResult,
  useBeginManualSubmissionMultipleChoiceAssessmentAttempt,
  useDiscardMultipleChoiceAssessmentAttempt,
  usePauseMultipleChoiceAssessmentAttempt,
  useReadMultipleChoiceAssessmentAttemptTime,
  useResumeMultipleChoiceAssessmentAttempt,
  useScanMultipleChoiceAssessmentAttemptAnswerSheet,
  useSubmitMultipleChoiceAssessmentAttempt,
} from '../../api/useMultipleChoiceAssessments';

import styles from './MultipleChoiceAssessmentAttempt.module.scss';

export type MultipleChoiceAssessmentAttemptProps = BaseComponentProps & {
  /**
   * Creates — or resumes — the attempt, and answers with it. Only called once the user
   * has said how they are answering, because the server starts counting the moment the
   * attempt exists. The chosen mode comes along so the attempt is created with it.
   * Rejecting closes the attempt; the error toast has been shown by the axios
   * interceptor already.
   */
  onStart: (
    mode: AssessmentMode,
  ) => Promise<CreateMultipleChoiceAssessmentAttemptCommandResult>;
  /**
   * The mode the attempt is answered in, when it is not the user's to pick: what an
   * already-created attempt was created with — a paused one being resumed — or the only
   * mode an assessment offers. Given it, the mode question is not asked and the attempt
   * starts straight away.
   */
  mode?: AssessmentMode;
  onFinish: (
    attempt: CreateMultipleChoiceAssessmentAttemptCommandResult,
  ) => void;
  onClose: () => void;
};

const MODES: AssessmentMode[] = ['interactive', 'manual'];

const MODE_IMAGES: Record<AssessmentMode, string> = {
  interactive: InteractiveImg,
  manual: ManualImg,
};

const ANSWER_SHEET_FILE = 'plantilla-de-respuestas-opositandopn.pdf';

const MODE_SUBTITLES: Record<AssessmentMode, string> = {
  interactive:
    'Marcas cada respuesta en la pantalla y envías el simulacro al terminar',
  manual:
    'Respondes en nuestra plantilla y trasladas tus respuestas al terminar',
};

/**
 * Turns the answers the assessment reports (question id + 1-based choice index,
 * 0 meaning unanswered) into the question-attempt ids and choice ids the API expects.
 */
function toQuestionAttempts(
  attempt: CreateMultipleChoiceAssessmentAttemptCommandResult,
  questionAnswers: QuestionAnswerType[],
) {
  return questionAnswers.map((questionAnswer) => {
    const questionAttempt = attempt.questionAttempts.find(
      (question) => question.questionId === questionAnswer.id,
    )!;

    return {
      id: questionAttempt.id,
      answer:
        questionAnswer.answerIndex === 0
          ? ''
          : questionAttempt.choices[questionAnswer.answerIndex - 1].id,
      flagged: questionAnswer.flagged ?? false,
    };
  });
}

function MultipleChoiceAssessmentAttempt({
  onStart,
  onFinish,
  onClose,
  mode: startedMode,
}: MultipleChoiceAssessmentAttemptProps) {
  const { profile } = useProfile();
  const { configuration } = useConfiguration();

  const [mode, setMode] = useState<AssessmentMode>(
    startedMode ?? 'interactive',
  );

  // The mode question is what is on screen until the user starts, because the server
  // starts counting the moment the attempt exists — so nothing is created before then.
  // An attempt that arrives with its mode was created already and answers the question.
  const [isStarted, setIsStarted] = useState(startedMode !== undefined);

  // The question is a dialog inside the dialog that holds the whole attempt, and it
  // portals out of it — so it does not hear the backdrop closing and would sit there at
  // full opacity until the backdrop finished fading and unmounted it. Fading it out at
  // the same moment the backdrop starts makes leaving feel immediate.
  const [isDismissed, setIsDismissed] = useState(false);

  const dismiss = () => {
    setIsDismissed(true);
    onClose();
  };

  const [attempt, setAttempt] =
    useState<CreateMultipleChoiceAssessmentAttemptCommandResult>();

  const [attemptResult, setAttemptResult] =
    useState<AssessmentAttemptViewModel<MultipleChoiceQuestionViewModel>>();

  const {
    mutate: submitMultipleChoiceAssessmentAttempt,
    isPending: isSubmitPending,
  } = useSubmitMultipleChoiceAssessmentAttempt();

  const { mutateAsync: beginManualSubmissionMultipleChoiceAssessmentAttempt } =
    useBeginManualSubmissionMultipleChoiceAssessmentAttempt();

  const { mutateAsync: scanMultipleChoiceAssessmentAttemptAnswerSheet } =
    useScanMultipleChoiceAssessmentAttemptAnswerSheet();

  const isPending = isSubmitPending;

  const { mutateAsync: pauseMultipleChoiceAssessmentAttempt } =
    usePauseMultipleChoiceAssessmentAttempt();

  const { mutateAsync: resumeMultipleChoiceAssessmentAttempt } =
    useResumeMultipleChoiceAssessmentAttempt();

  const { mutateAsync: discardMultipleChoiceAssessmentAttempt } =
    useDiscardMultipleChoiceAssessmentAttempt();

  const readMultipleChoiceAssessmentAttemptTime =
    useReadMultipleChoiceAssessmentAttemptTime();

  const { mutateAsync: bookmarkMultipleChoiceQuestion } =
    useBookmarkMultipleChoiceQuestion();

  const { mutateAsync: unbookmarkMultipleChoiceQuestion } =
    useUnbookmarkMultipleChoiceQuestion();

  const attemptQuestions = useMemo(
    () =>
      attempt?.questionAttempts.map((question) => ({
        id: question.questionId,
        text: question.text,
        options: question.choices.map((choice) => choice.text),
        // Choices are 1-based in the assessment, 0 meaning unanswered, so an answer
        // already given comes back as the position of its choice plus one.
        selectedOptionIndex: question.answer
          ? question.choices.findIndex(
              (choice) => choice.id === question.answer,
            ) + 1
          : 0,
        bookmarked: question.bookmarked ?? false,
        flagged: question.flagged ?? false,
        imageUrl: question.imageUrl,
      })),
    [attempt],
  );

  const run = useCallback(
    async (selectedMode: AssessmentMode) => {
      try {
        setAttempt(await onStart(selectedMode));
      } catch {
        // The attempt never came into being, so there is nothing to sit — the error
        // toast has been shown already and the user is put back where they came from.
        onClose();
      }
    },
    [onStart, onClose],
  );

  const start = () => {
    setIsStarted(true);
    void run(mode);
  };

  const isRunning = useRef(false);

  useEffect(() => {
    if (startedMode === undefined || isRunning.current) {
      return;
    }

    isRunning.current = true;
    void run(startedMode);
  }, [startedMode, run]);

  const remainingTimeFrom = async (
    read: (request: {
      subjectId: string;
      assessmentAttemptId: string;
    }) => Promise<{ remainingTime: number }>,
  ) => {
    if (!attempt) {
      return;
    }

    const { remainingTime } = await read({
      subjectId: attempt.subject.id,
      assessmentAttemptId: attempt.id,
    });

    return remainingTime;
  };

  return (
    <>
      {attemptResult && attempt ? (
        <AssessmentAttemptResult
          className={styles['result']}
          score={attemptResult.score}
          description={attempt.name ?? 'Simulacro'}
          elapsedTime={attemptResult.elapsedTime}
          answerRate={{
            correct: attemptResult.questionAttempts.filter(
              (questionAttempt) =>
                questionAttempt.answer ===
                questionAttempt.correction.correctAnswer,
            ).length,
            incorrect: attemptResult.questionAttempts.filter(
              (questionAttempt) =>
                questionAttempt.answer !== '' &&
                questionAttempt.answer !==
                  questionAttempt.correction.correctAnswer,
            ).length,
            skipped: attemptResult.questionAttempts.filter(
              (questionAttempt) => questionAttempt.answer === '',
            ).length,
          }}
        >
          <Button
            className={styles['button']}
            variant="outline-primary"
            onClick={() => onFinish(attempt)}
          >
            Ver respuestas
          </Button>
        </AssessmentAttemptResult>
      ) : !isStarted ? (
        <Dialog
          open={!isDismissed}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              dismiss();
            }
          }}
        >
          <DialogContent>
            <ActionDialogContent
              className={styles['mode-dialog']}
              icon={null}
              title="¿Cómo vas a responder?"
              media={
                <div className={styles['mode-images']}>
                  {MODES.map((modeImage) => (
                    <img
                      key={modeImage}
                      className={classnames(
                        styles['mode-image'],
                        mode === modeImage && styles['mode-image--selected'],
                      )}
                      src={MODE_IMAGES[modeImage]}
                      alt=""
                    />
                  ))}
                </div>
              }
              subtitle={MODE_SUBTITLES[mode]}
            >
              <div className={styles['mode']}>
                <ButtonGroup
                  label="Cómo vas a responder"
                  options={[
                    {
                      value: 'interactive',
                      label: 'En pantalla',
                      selected: mode === 'interactive',
                    },
                    {
                      value: 'manual',
                      label: 'En papel',
                      selected: mode === 'manual',
                    },
                  ]}
                  onSelect={(value) => setMode(value as AssessmentMode)}
                />
              </div>
              <ActionDialogButtons>
                {mode === 'manual' && (
                  <ButtonLink
                    variant="outline-primary"
                    prefixIcon={<IoDocumentTextOutline />}
                    href={`${configuration.app?.filesUrl}/${ANSWER_SHEET_FILE}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Plantilla
                  </ButtonLink>
                )}
                <Button
                  variant="primary"
                  onClick={start}
                  suffixIcon={<IoArrowForwardCircleOutline />}
                >
                  Empezar
                </Button>
              </ActionDialogButtons>
            </ActionDialogContent>
          </DialogContent>
        </Dialog>
      ) : attempt && attemptQuestions ? (
        <MultipleChoiceAssessment
          className={styles['questionnaire']}
          questions={attemptQuestions}
          // An attempt served by an API that predates the countdown starts from the
          // whole time limit rather than showing no countdown at all.
          timeInSeconds={attempt.remainingTime ?? attempt.timeLimit}
          instructions={
            attempt.description
              ? {
                  text: attempt.description,
                  imageUrl: attempt.imageUrl,
                }
              : undefined
          }
          isSubmitting={isPending}
          canPause={attempt.canPause}
          mode={mode}
          // Reopened with its sitting already over — what is left is uploading the
          // answers, so it opens at the form rather than at the questions.
          isTranscribing={attempt.status === 'submitting'}
          onStartSubmitting={async () => {
            await beginManualSubmissionMultipleChoiceAssessmentAttempt({
              subjectId: attempt.subject.id,
              assessmentAttemptId: attempt.id,
            });
          }}
          onScanAnswerSheet={async (base64Image) => {
            const { questionAttempts } =
              await scanMultipleChoiceAssessmentAttemptAnswerSheet({
                subjectId: attempt.subject.id,
                assessmentAttemptId: attempt.id,
                base64Image,
              });

            return questionAttempts;
          }}
          onSubmit={(questionAnswers) => {
            submitMultipleChoiceAssessmentAttempt(
              {
                subjectId: attempt.subject.id,
                assessmentAttemptId: attempt.id,
                questionAttempts: toQuestionAttempts(attempt, questionAnswers),
              },
              {
                onSuccess: (
                  data: AssessmentAttemptViewModel<MultipleChoiceQuestionViewModel>,
                ) => {
                  setAttemptResult(data);
                },
              },
            );
          }}
          onPause={async (questionAnswers) => {
            await pauseMultipleChoiceAssessmentAttempt({
              subjectId: attempt.subject.id,
              assessmentAttemptId: attempt.id,
              questionAttempts: toQuestionAttempts(attempt, questionAnswers),
            });
          }}
          onResume={() =>
            remainingTimeFrom(resumeMultipleChoiceAssessmentAttempt)
          }
          onSyncTime={() =>
            remainingTimeFrom(readMultipleChoiceAssessmentAttemptTime)
          }
          onDiscard={async () => {
            try {
              await discardMultipleChoiceAssessmentAttempt({
                subjectId: attempt.subject.id,
                assessmentAttemptId: attempt.id,
              });
            } catch {
              // The attempt could not be discarded — the error toast has been shown
              // already and the assessment closes either way.
            }

            onClose();
          }}
          onClose={onClose}
          onBookmark={
            profile.subscription?.isActive
              ? async (id) => {
                  await bookmarkMultipleChoiceQuestion({
                    subjectId: attempt.subject.id,
                    questionId: id,
                  });
                }
              : undefined
          }
          onUnbookmark={
            profile.subscription?.isActive
              ? async (id) => {
                  await unbookmarkMultipleChoiceQuestion({
                    subjectId: attempt.subject.id,
                    questionId: id,
                  });
                }
              : undefined
          }
        />
      ) : (
        <Loading />
      )}
    </>
  );
}

export { MultipleChoiceAssessmentAttempt };
